import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export type AdminUser = { id: string; email: string; name: string; role: "admin" | "oversight" | "dow_reviewer"; permissions: string[]; mfa_enabled: boolean; tours_done?: string[] };
type Ctx = { user: AdminUser | null | false; setUser: (u: AdminUser | false) => void; logout: () => Promise<void>; can: (p: string) => boolean };

const AuthContext = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null | false>(null);

  useEffect(() => {
    const check = async () => {
      if (!window.location.pathname.startsWith("/admin")) return setUser(false);
      try {
        setUser((await api.get("/auth/me")).data);
      } catch {
        try {
          await api.post("/auth/refresh");
          setUser((await api.get("/auth/me")).data);
        } catch {
          setUser(false);
        }
      }
    };
    check();
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(false);
  }, []);

  const can = useCallback((p: string) => !!user && (user.role === "admin" || (user.permissions || []).includes(p)), [user]);

  return <AuthContext.Provider value={{ user, setUser, logout, can }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
